import React, { useState, useEffect } from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  Platform } from 'react-native'
import { ArrowLeft, RefreshCw, Search, AlertTriangle, Check, X, DollarSign } from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { api } from '../lib/api'
import { useLanguage } from '@fliisi/i18n'
import { formatCurrency } from '../lib/stripe'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Sheet,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketRefunds'>
  route: RouteProp<RootStackParamList, 'MarketRefunds'>
}

interface Sale {
  id: string
  total_amount: number
  payment_method: string
  status: string
  created_at: string
  stripe_payment_intent_id?: string
  items: SaleItem[]
}

interface SaleItem {
  id: string
  price: number
  product: {
    id: string
    title_fi: string
    title_en: string
    status: string
  }
  seller: {
    id: string
    full_name: string
    email: string
  }
  refunded: boolean
  refund_amount?: number
}

interface RefundableItem {
  saleItemId: string
  productTitle: string
  price: number
  sellerName: string
}

export default function MarketRefundsScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t, language } = useLanguage()

  const [loading, setLoading] = useState(true)
  const [sales, setSales] = useState<Sale[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [refundReason, setRefundReason] = useState('')
  const [processing, setProcessing] = useState(false)
  const [marketName, setMarketName] = useState('')

  useEffect(() => {
    fetchMarketAndSales()
  }, [marketId])

  const fetchMarketAndSales = async () => {
    setLoading(true)
    try {
      // Fetch market name
      const { data: market } = await supabase
        .from('flea_markets')
        .select('name')
        .eq('id', marketId)
        .single()

      if (market) setMarketName(market.name)

      // Fetch recent sales with items
      const { data: salesData, error } = await supabase
        .from('sales')
        .select(`
          id,
          total_amount,
          payment_method,
          status,
          created_at,
          sale_items (
            id,
            price,
            refunded,
            refund_amount,
            product:products (
              id,
              title_fi,
              title_en,
              status
            ),
            seller:users!sale_items_seller_id_fkey (
              id,
              full_name,
              email
            )
          )
        `)
        .eq('flea_market_id', marketId)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error

      // Get payment intent IDs from payments table
      const saleIds = salesData?.map(s => s.id) || []
      const { data: payments } = await supabase
        .from('payments')
        .select('sale_id, stripe_payment_intent_id')
        .in('sale_id', saleIds)

      const paymentMap = new Map(payments?.map(p => [p.sale_id, p.stripe_payment_intent_id]) || [])

      const formattedSales: Sale[] = (salesData || []).map(sale => ({
        id: sale.id,
        total_amount: sale.total_amount,
        payment_method: sale.payment_method,
        status: sale.status,
        created_at: sale.created_at,
        stripe_payment_intent_id: paymentMap.get(sale.id),
        items: (sale.sale_items || []).map((item: any) => ({
          id: item.id,
          price: item.price,
          product: Array.isArray(item.product) ? item.product[0] : item.product,
          seller: Array.isArray(item.seller) ? item.seller[0] : item.seller,
          refunded: item.refunded || false,
          refund_amount: item.refund_amount })) }))

      setSales(formattedSales)
    } catch (error: any) {
      console.error('Error fetching sales:', error)
      showAlert(t('error') || 'Error', error.message)
    } finally {
      setLoading(false)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}: ${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  const confirmAlert = (title: string, message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      if (window.confirm(`${title}\n\n${message}`)) {
        onConfirm()
      }
    } else {
      Alert.alert(title, message, [
        { text: t('cancel') || 'Cancel', style: 'cancel' },
        { text: t('confirm') || 'Confirm', style: 'destructive', onPress: onConfirm },
      ])
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const locale = language === 'fi' ? 'fi-FI' : 'en-US'
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit' })
  }

  const getProductTitle = (item: SaleItem) => {
    return language === 'fi'
      ? item.product?.title_fi || item.product?.title_en || 'Unknown'
      : item.product?.title_en || item.product?.title_fi || 'Unknown'
  }

  const filteredSales = sales.filter(sale => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      sale.id.toLowerCase().includes(query) ||
      sale.items.some(item =>
        getProductTitle(item).toLowerCase().includes(query) ||
        item.seller?.full_name?.toLowerCase().includes(query)
      )
    )
  })

  const openRefundModal = (sale: Sale) => {
    setSelectedSale(sale)
    setSelectedItems(new Set())
    setRefundReason('')
    setShowRefundModal(true)
  }

  const toggleItemSelection = (itemId: string) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId)
    } else {
      newSelected.add(itemId)
    }
    setSelectedItems(newSelected)
  }

  const getRefundTotal = () => {
    if (!selectedSale) return 0
    return selectedSale.items
      .filter(item => selectedItems.has(item.id) && !item.refunded)
      .reduce((sum, item) => sum + item.price, 0)
  }

  const processRefund = async () => {
    if (!selectedSale || selectedItems.size === 0) return

    const refundAmount = getRefundTotal()
    if (refundAmount <= 0) {
      showAlert(t('error') || 'Error', t('no_items_to_refund') || 'No items selected for refund')
      return
    }

    confirmAlert(
      t('confirm_refund') || 'Confirm Refund',
      `${t('refund_confirmation') || 'Are you sure you want to refund'} ${formatCurrency(refundAmount)}?`,
      async () => {
        setProcessing(true)
        try {
          // Call refund via trellis
          await api.processRefund.fetch({
            sale_id: selectedSale.id,
            sale_item_ids: Array.from(selectedItems),
            reason: refundReason || 'Customer request' })

          showAlert(t('success') || 'Success', t('refund_processed') || 'Refund processed successfully')
          setShowRefundModal(false)
          fetchMarketAndSales()
        } catch (error: any) {
          console.error('Refund error:', error)
          showAlert(t('error') || 'Error', error.message)
        } finally {
          setProcessing(false)
        }
      }
    )
  }

  const renderSaleCard = (sale: Sale) => {
    const hasRefundedItems = sale.items.some(item => item.refunded)
    const allRefunded = sale.items.every(item => item.refunded)
    const refundableItems = sale.items.filter(item => !item.refunded)
    const canRefund = refundableItems.length > 0 && sale.payment_method !== 'cash'

    return (
      <View key={sale.id} style={styles.saleCard}>
        <View style={styles.saleHeader}>
          <View>
            <Text style={styles.saleDate}>{formatDate(sale.created_at)}</Text>
            <Text style={styles.saleId}>#{sale.id.slice(-8)}</Text>
          </View>
          <View style={styles.saleHeaderRight}>
            <Text style={styles.saleTotal}>{formatCurrency(sale.total_amount)}</Text>
            <View style={[
              styles.paymentMethodBadge,
              sale.payment_method === 'cash' && styles.cashBadge,
              sale.payment_method === 'card' && styles.cardBadge,
              sale.payment_method === 'stripe_terminal' && styles.terminalBadge,
            ]}>
              <Text style={styles.paymentMethodText}>
                {sale.payment_method === 'cash' ? (t('cash') || 'Cash') :
                 sale.payment_method === 'card' ? (t('card') || 'Card') :
                 sale.payment_method === 'stripe_terminal' ? 'Terminal' :
                 sale.payment_method}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.itemsList}>
          {sale.items.map(item => (
            <View key={item.id} style={[styles.itemRow, item.refunded && styles.refundedItem]}>
              <View style={styles.itemInfo}>
                <Text style={[styles.itemTitle, item.refunded && styles.refundedText]} numberOfLines={1}>
                  {getProductTitle(item)}
                </Text>
                <Text style={styles.itemSeller}>{item.seller?.full_name || 'Unknown'}</Text>
              </View>
              <View style={styles.itemPriceContainer}>
                <Text style={[styles.itemPrice, item.refunded && styles.refundedText]}>
                  {formatCurrency(item.price)}
                </Text>
                {item.refunded && (
                  <View style={styles.refundedBadge}>
                    <Text style={styles.refundedBadgeText}>{t('refunded') || 'Refunded'}</Text>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>

        {canRefund && (
          <TouchableOpacity
            style={styles.refundButton}
            onPress={() => openRefundModal(sale)}
          >
            <DollarSign size={16} color={theme.colors.status.error} />
            <Text style={styles.refundButtonText}>{t('issue_refund') || 'Issue Refund'}</Text>
          </TouchableOpacity>
        )}

        {sale.payment_method === 'cash' && !allRefunded && (
          <View style={styles.cashWarning}>
            <AlertTriangle size={14} color="#FF9800" />
            <Text style={styles.cashWarningText}>
              {t('cash_refund_manual') || 'Cash sales require manual refund'}
            </Text>
          </View>
        )}

        {allRefunded && (
          <View style={styles.fullyRefundedBanner}>
            <Check size={14} color="#4CAF50" />
            <Text style={styles.fullyRefundedText}>
              {t('fully_refunded') || 'Fully Refunded'}
            </Text>
          </View>
        )}
      </View>
    )
  }

  const renderRefundModal = () => {
    if (!selectedSale) return null

    const refundableItems = selectedSale.items.filter(item => !item.refunded)

    return (
      <Sheet open={showRefundModal} onOpenChange={setShowRefundModal} noHandle>
          <View style={{ padding: 20 }}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('issue_refund') || 'Issue Refund'}</Text>
              <TouchableOpacity onPress={() => setShowRefundModal(false)}>
                <X size={24} color={theme.colors.text.primary} />
              </TouchableOpacity>
            </View>

            <Text style={styles.modalSubtitle}>
              {t('select_items_to_refund') || 'Select items to refund'}
            </Text>

            <ScrollView style={styles.modalItemsList}>
              {refundableItems.map(item => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.modalItem,
                    selectedItems.has(item.id) && styles.modalItemSelected,
                  ]}
                  onPress={() => toggleItemSelection(item.id)}
                >
                  <View style={styles.checkbox}>
                    {selectedItems.has(item.id) && (
                      <Check size={16} color="#fff" />
                    )}
                  </View>
                  <View style={styles.modalItemInfo}>
                    <Text style={styles.modalItemTitle}>{getProductTitle(item)}</Text>
                    <Text style={styles.modalItemSeller}>{item.seller?.full_name}</Text>
                  </View>
                  <Text style={styles.modalItemPrice}>{formatCurrency(item.price)}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={styles.refundReasonContainer}>
              <Text style={styles.refundReasonLabel}>{t('refund_reason') || 'Reason (optional)'}</Text>
              <TextInput
                style={styles.refundReasonInput}
                value={refundReason}
                onChangeText={setRefundReason}
                placeholder={t('enter_reason') || 'Enter reason...'}
                placeholderTextColor={theme.colors.text.secondary}
                multiline
              />
            </View>

            <View style={styles.refundSummary}>
              <Text style={styles.refundSummaryLabel}>{t('refund_total') || 'Refund Total'}</Text>
              <Text style={styles.refundSummaryAmount}>{formatCurrency(getRefundTotal())}</Text>
            </View>

            <TouchableOpacity
              style={[
                styles.processRefundButton,
                (processing || selectedItems.size === 0) && styles.buttonDisabled,
              ]}
              onPress={processRefund}
              disabled={processing || selectedItems.size === 0}
            >
              {processing ? (
                <Spinner color="#fff" />
              ) : (
                <Text style={styles.processRefundText}>
                  {t('process_refund') || 'Process Refund'}
                </Text>
              )}
            </TouchableOpacity>
          </View>
      </Sheet>
    )
  }

  return (
    <View style={styles.container}>
      {renderRefundModal()}

      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowLeft size={20} color={theme.colors.brand.primary} />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{t('refunds') || 'Refunds'}</Text>
          <Text style={styles.headerSubtitle}>{marketName}</Text>
        </View>
        <TouchableOpacity style={styles.refreshButton} onPress={fetchMarketAndSales}>
          <RefreshCw size={20} color={theme.colors.brand.primary} />
        </TouchableOpacity>
      </View>

      <View style={styles.searchContainer}>
        <Search size={18} color={theme.colors.text.secondary} />
        <TextInput
          style={styles.searchInput}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder={t('search_sales') || 'Search sales...'}
          placeholderTextColor={theme.colors.text.secondary}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <X size={18} color={theme.colors.text.secondary} />
          </TouchableOpacity>
        )}
      </View>

      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : filteredSales.length === 0 ? (
        <View style={styles.emptyContainer}>
          <DollarSign size={48} color={theme.colors.text.secondary} />
          <Text style={styles.emptyText}>
            {searchQuery
              ? (t('no_sales_found') || 'No sales found')
              : (t('no_sales_yet') || 'No sales yet')}
          </Text>
        </View>
      ) : (
        <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
          {filteredSales.map(renderSaleCard)}
        </ScrollView>
      )}
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.borderLight },
  backButton: {
    padding: 8 },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 8 },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  refreshButton: {
    padding: 8 },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 12,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    fontSize: 16,
    color: theme.colors.text.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40 },
  emptyText: {
    fontSize: 16,
    color: theme.colors.text.secondary,
    marginTop: 16,
    textAlign: 'center' },
  content: {
    flex: 1 },
  contentContainer: {
    padding: 16,
    paddingBottom: 100 },
  saleCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: theme.borderRadius.lg,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.ui.border },
  saleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12 },
  saleDate: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  saleId: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  saleHeaderRight: {
    alignItems: 'flex-end' },
  saleTotal: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  paymentMethodBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 4 },
  cashBadge: {
    backgroundColor: '#E8F5E9' },
  cardBadge: {
    backgroundColor: '#E3F2FD' },
  terminalBadge: {
    backgroundColor: '#F3E5F5' },
  paymentMethodText: {
    fontSize: 11,
    fontWeight: '600',
    color: theme.colors.text.secondary },
  itemsList: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight,
    paddingTop: 12 },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8 },
  refundedItem: {
    opacity: 0.6 },
  itemInfo: {
    flex: 1 },
  itemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  itemSeller: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  itemPriceContainer: {
    alignItems: 'flex-end' },
  itemPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  refundedText: {
    textDecorationLine: 'line-through' },
  refundedBadge: {
    backgroundColor: '#FFEBEE',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginTop: 4 },
  refundedBadgeText: {
    fontSize: 10,
    color: '#C62828',
    fontWeight: '600' },
  refundButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFEBEE',
    paddingVertical: 12,
    borderRadius: theme.borderRadius.md,
    marginTop: 12,
    gap: 8 },
  refundButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.status.error },
  cashWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF8E1',
    paddingVertical: 10,
    borderRadius: theme.borderRadius.md,
    marginTop: 12,
    gap: 8 },
  cashWarningText: {
    fontSize: 12,
    color: '#F57C00' },
  fullyRefundedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E8F5E9',
    paddingVertical: 10,
    borderRadius: theme.borderRadius.md,
    marginTop: 12,
    gap: 8 },
  fullyRefundedText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4CAF50' },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: theme.colors.background.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '80%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12 },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.text.primary },
  modalSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: 16 },
  modalItemsList: {
    maxHeight: 250 },
  modalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    marginBottom: 8 },
  modalItemSelected: {
    backgroundColor: '#E8F5E9',
    borderWidth: 1,
    borderColor: '#4CAF50' },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#4CAF50',
    marginRight: 12 },
  modalItemInfo: {
    flex: 1 },
  modalItemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.primary },
  modalItemSeller: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  modalItemPrice: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text.primary },
  refundReasonContainer: {
    marginTop: 16 },
  refundReasonLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.text.secondary,
    marginBottom: 8 },
  refundReasonInput: {
    backgroundColor: theme.colors.background.secondary,
    borderRadius: theme.borderRadius.md,
    padding: 12,
    fontSize: 14,
    color: theme.colors.text.primary,
    minHeight: 60,
    textAlignVertical: 'top' },
  refundSummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.borderLight },
  refundSummaryLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colors.text.secondary },
  refundSummaryAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.status.error },
  processRefundButton: {
    backgroundColor: theme.colors.status.error,
    paddingVertical: 16,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    marginTop: 20 },
  buttonDisabled: {
    opacity: 0.5 },
  processRefundText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' } })
